import { useCreate, useNotification, useOne, useUpdate } from "@refinedev/core";
import { Flex, Input, Typography } from "antd";
import { useSession } from "hooks/useSession";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PreviewTitle } from "../../brandkit/components/preview/PreviewTitle";
import {
  ContactResponse,
  FaceProfileResponse,
  PresenterProfileResponse,
  VoiceProfileResponse,
} from "../../types";
import {
  AvatarCloneModalType,
  AvatarCloneVoiceAndFaceModal,
} from "./AvatarCloneVoiceAndFaceModal";
import { StyledAvatarContainer } from "./AvatarSummary";
import FaceSelector from "./FaceSelector";
import { FaceProfileCreationResponse } from "./types";
import VoiceSelector from "./VoiceSelector";
import QRCodeDisplay from "components/QRCodeDisplay/QRCodeDisplay/";
import { useFeature } from "hooks/useFeature/";
import { FeatureFlags } from "@/constants";
import { getContactName } from "./helpers";
import { useTranslate } from "#src/hooks";
import { useAntTheme } from "hooks/useAntTheme/";
import { radius } from "@libertify/ui";

export const AVATAR_ITEM_PREVIEW_BORDER_RADIUS = radius.box;

interface AvatarDetailsProps {
  id?: string;
  contact?: ContactResponse;
  presenter?: { data: PresenterProfileResponse | null };
  setSubmitButtonEnabled?: (value: boolean) => void;
  saveCallback?: React.MutableRefObject<(() => Promise<void>) | undefined>;
  voiceOnly?: boolean;
  scriptLanguage?: string;
  onQRComplete?: () => void;
  onSuccess?: (id: string, updatedPresenter?: PresenterProfileResponse) => void;
  isNewAvatar?: boolean;
  style?: React.CSSProperties;
}

export const AvatarDetails = ({
  id,
  contact,
  presenter,
  setSubmitButtonEnabled,
  saveCallback,
  voiceOnly,
  scriptLanguage,
  onQRComplete,
  onSuccess,
  isNewAvatar,
  style,
}: AvatarDetailsProps) => {
  const t = useTranslate();
  const { isMobile } = useAntTheme();
  const { open } = useNotification();
  const { currentOrganization } = useSession();
  const shouldCreateAvatar = useMemo(
    () => !id || isNewAvatar,
    [id, isNewAvatar]
  );

  const { mutateAsync: createNewPresenter } =
    useCreate<PresenterProfileResponse>();
  const { mutateAsync: mutateContact } = useUpdate<ContactResponse>();

  const name = useMemo(() => {
    return getContactName(contact);
  }, [contact]);

  const nameInputStyle = useMemo(
    () => ({
      height: isMobile ? 35 : 50,
      fontSize: isMobile ? 18 : 20,
    }),
    [isMobile]
  );

  const faceProfile = presenter?.data?.face_profile;
  const voiceProfile = presenter?.data?.voice_profile;
  const hasCallback = useRef(false);
  // We set the save callback so that we can call the update/create
  // methods from outside such as when clicking a save button in
  // Edit/Create from Refine or when using this component inside a
  // modal.
  const prevSelectedVoice = useRef<VoiceProfileResponse>();

  // Those two are partial records that do not have assets yet
  const [clonedFaceResponse, setClonedFaceResponse] =
    useState<FaceProfileCreationResponse>();
  const [clonedVoiceResponse, setClonedVoiceResponse] =
    useState<VoiceProfileResponse>();

  const [contactName, setContactName] = useState<string | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<
    VoiceProfileResponse | undefined
  >(undefined);
  const [selectedFace, setSelectedFace] = useState<
    Partial<FaceProfileResponse> | undefined
  >(undefined);
  const [updatedPresenter, setUpdatedPresenter] =
    useState<PresenterProfileResponse>();
  const { mutateAsync: createNewContact } = useCreate<ContactResponse>();

  const [error, setError] = useState<boolean>(false);
  const [currentModal, setCurrentModal] = useState<AvatarCloneModalType>(null);
  const closeModalCallback = useRef((_: Partial<FaceProfileResponse>) => {});

  const currentVoice = useMemo(
    () => selectedVoice ?? voiceProfile,
    [selectedVoice, voiceProfile]
  );
  const currentFace = useMemo(
    () => selectedFace ?? faceProfile,
    [selectedFace, faceProfile]
  );

  const currentName = useMemo(() => contactName ?? name, [contactName, name]);

  const { isEnabled: isEnabledMobileCloning } = useFeature(
    FeatureFlags.MobileCloning
  );

  const validateName = useCallback(
    (currentName: string) => {
      const noName: boolean = !currentName || currentName.trim().length === 0;
      setSubmitButtonEnabled?.(!noName);
      return !noName;
    },
    [setSubmitButtonEnabled]
  );

  useEffect(() => {
    const formIsValid = currentVoice && validateName(currentName);
    setSubmitButtonEnabled?.(!!formIsValid);
    // show error when voice is selected but name is empty
    if (!!currentVoice && !formIsValid) setError(true);
  }, [currentVoice, setSubmitButtonEnabled, currentName, validateName]);

  const showVoiceErrorNotification = useCallback(
    (messageKey: string) => {
      open?.({
        description: t("components.ContactWithPresenter.PageComponent.error"),
        message: t(messageKey),
        type: "error",
      });
    },
    [open, t]
  );

  const isVoiceSelected = useCallback(
    (showNotification = true) => {
      if (!currentVoice) {
        if (showNotification) {
          showVoiceErrorNotification(
            "components.ContactWithPresenter.PageComponent.youNeedTo"
          );
        }
        return false;
      }
      return true;
    },
    [currentVoice, showVoiceErrorNotification]
  );

  const hasUnsavedPresenterChanges = useMemo(() => {
    if (!presenter?.data) return false;
    return (
      presenter.data.voice_profile.id !== currentVoice?.id ||
      presenter.data.face_profile?.id !== currentFace?.id
    );
  }, [presenter?.data, currentVoice, currentFace]);

  const { data: clonedVoice } = useOne<VoiceProfileResponse>({
    resource: `media/voice_profiles`,
    id: clonedVoiceResponse?.id,
    queryOptions: {
      enabled: !!clonedVoiceResponse,
    },
    errorNotification: false,
  });

  useEffect(() => {
    // The cloning Success signal arrives over WebSocket inside useVoiceCloning.
    // By the time setClonedVoiceResponse is called here, BE has already finished
    // cloning, so the useOne fetch is guaranteed to return a usable profile —
    // no need to additionally gate on preview_asset_path (which doesn't exist
    // on the new VoiceProfileResponse anyway). While the fetch is in flight,
    // keep the submit button disabled so the user can't act on stale state.
    if (clonedVoice?.data) {
      setSelectedVoice(clonedVoice.data);
      setClonedVoiceResponse(undefined);
      setSubmitButtonEnabled?.(true);
    } else if (clonedVoiceResponse) {
      const hasValidVoice = selectedVoice ?? voiceProfile;
      if (!hasValidVoice) {
        setSubmitButtonEnabled?.(false);
      }
    }
  }, [
    clonedVoice?.data,
    clonedVoiceResponse,
    selectedVoice,
    voiceProfile,
    setSubmitButtonEnabled,
  ]);

  const createPresenter = useCallback(async () => {
    const presenter = await createNewPresenter({
      resource: `media/${currentOrganization?.id}/presenter_profiles`,
      values: {
        name: currentName,
        face_profile_id: currentFace?.id,
        voice_profile_id: currentVoice?.id,
      },
      successNotification: false,
    });
    if (!presenter?.data) {
      throw new Error("Error: presented not found");
    }
    return presenter.data;
  }, [
    createNewPresenter,
    currentName,
    currentFace,
    currentVoice,
    currentOrganization?.id,
  ]);

  const createContact = useCallback(
    async (presenter: PresenterProfileResponse) => {
      const [firstname, ...lastname] = currentName.split(" ");
      const values = {
        firstname,
        lastname: lastname.join(" "),
        presenter_id: presenter.id,
      };

      if (!lastname) {
        values.firstname = firstname;
        values.lastname = "";
      }
      const contact = await createNewContact({
        resource: `media/${currentOrganization?.id}/contacts`,
        values,
      });
      if (!contact?.data) return;
      onSuccess?.(contact.data.id);
    },
    [createNewContact, currentOrganization?.id, currentName, onSuccess]
  );

  const updateContact = async (id: string) => {
    const [firstname, ...lastname] = currentName.split(" ");
    if (!isVoiceSelected()) return;
    let presenterUpdated;
    if (hasUnsavedPresenterChanges) {
      presenterUpdated = await createPresenter();
      setUpdatedPresenter(presenterUpdated);
    }
    await mutateContact({
      resource: `media/${currentOrganization?.id}/contacts`,
      id,
      values: {
        firstname,
        lastname: lastname.join(" "),
        presenter_id: presenterUpdated?.id ?? presenter?.data?.id,
      },
    });
    setSelectedFace(undefined);
    setSelectedVoice(undefined);
    onSuccess?.(id, presenterUpdated ?? presenter?.data ?? undefined);
  };

  const createPresenterAndContact = async () => {
    if (!isVoiceSelected()) return;
    if (currentName?.trim().length === 0) {
      return;
    }
    try {
      const presenterUpdated =
        updatedPresenter ?? presenter?.data ?? (await createPresenter());
      await createContact(presenterUpdated);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (shouldCreateAvatar) {
      if (prevSelectedVoice.current === undefined && selectedVoice) {
        prevSelectedVoice.current = selectedVoice;
      }
      if (
        hasCallback.current === false ||
        prevSelectedVoice.current?.id !== selectedVoice?.id
      ) {
        prevSelectedVoice.current = selectedVoice;
        hasCallback.current = true;
      }

      saveCallback.current = async () => await createPresenterAndContact();
    } else {
      saveCallback.current = async () => await updateContact(id);
    }
    // saveCallback is not needed as dep because it is aMutableRefObject
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createContact,
    createPresenter,
    presenter?.data?.id,
    id,
    isVoiceSelected,
    mutateContact,
    currentOrganization?.id,
    hasUnsavedPresenterChanges,
    selectedVoice,
    selectedFace,
    contactName,
    name,
    currentName,
    shouldCreateAvatar,
  ]);

  // Once we get the assets, we can use those to display on the frontend
  const { data: clonedFace } = useOne<FaceProfileResponse>({
    resource: `media/face_profiles`,
    id: clonedFaceResponse?.face_profile_id,
    queryOptions: {
      enabled: !!clonedFaceResponse,
      refetchInterval: (data) => {
        if (!data?.data.extracted_asset_path) {
          return 1000; // Refetch every second until the face is ready
        }
        return false; // Stop refetching once we have the face ready
      },
      onError: () => {},
    },
    errorNotification: false,
  });

  // When we clone a face, we don't get the whole profile from the face
  // create form. Instead we only get its id. We insert an empty face
  // into the list with the spinner and then we replace that one with
  // the real full face object once we have the processed cloned face
  // image.
  useEffect(() => {
    setSelectedFace(clonedFace?.data);
  }, [clonedFace?.data]);

  return (
    <StyledAvatarContainer vertical gap={10} style={style}>
      {!id && isEnabledMobileCloning && (
        <Flex justify="center">
          <QRCodeDisplay
            // ONLY USE WHEN TESTING LOCALLY, FORWARD A PORT FROM YOUR COMPUTER AND SET THE ADDRESS HERE:
            //baseUrl="https://1clz1wwb-3000.uks1.devtunnels.ms/"
            voiceOnly={voiceOnly}
            language={scriptLanguage}
            onComplete={onQRComplete}
          />
        </Flex>
      )}
      <PreviewTitle
        title={t("components.ContactWithPresenter.AvatarDetails.title")}
      />
      <Flex gap={10} vertical style={{ marginBottom: 10 }}>
        <Typography.Text type="secondary" style={{ fontSize: 20 }}>
          {t("components.ContactWithPresenter.AvatarDetails.name")} *
        </Typography.Text>
        <Input
          value={currentName}
          placeholder={t(
            "components.ContactWithPresenter.PageComponent.contactNamePlaceholder"
          )}
          style={nameInputStyle}
          onChange={(e) => {
            setContactName(e.target.value);
            validateName(e.target.value);
            const noName = e.target.value.trim().length === 0;
            setError(noName);
          }}
        />
        {error && (
          <Typography.Text type="danger">
            {t("components.ContactWithPresenter.AvatarDetails.pleaseEnterName")}
          </Typography.Text>
        )}
      </Flex>

      {!voiceOnly && (
        <>
          <PreviewTitle
            title={t("components.ContactWithPresenter.AvatarDetails.face")}
          />
          <FaceSelector
            selectedFace={currentFace ?? undefined}
            cloneFaceResponse={clonedFaceResponse}
            clonedFace={clonedFace?.data}
            setSelectedFace={(face: FaceProfileResponse) => {
              setSelectedFace(face);
            }}
            setCurrentModal={setCurrentModal}
            disableFace={(value: boolean) => {
              if (value === false) {
                setSelectedFace(undefined);
              }
            }}
            setClonedFace={setClonedFaceResponse}
            isLoadingExistingContact={false}
            isNewAvatar={shouldCreateAvatar}
          />
        </>
      )}
      <PreviewTitle
        title={t("components.ContactWithPresenter.AvatarDetails.voice")}
      />
      <VoiceSelector
        setClonedVoice={setClonedVoiceResponse}
        cloneVoiceResponse={clonedVoiceResponse}
        clonedVoice={clonedVoice?.data}
        selectedVoice={currentVoice}
        setSelectedVoice={setSelectedVoice}
        setCurrentModal={setCurrentModal}
        isLoadingExistingContact={false}
        closeModalCallback={closeModalCallback}
        isNewAvatar={shouldCreateAvatar}
      />
      <AvatarCloneVoiceAndFaceModal
        currentModal={currentModal}
        setCurrentModal={setCurrentModal}
        open={open}
        setSelectedVoice={setSelectedVoice}
        setClonedVoiceResponse={setClonedVoiceResponse}
        setClonedFaceResponse={setClonedFaceResponse}
        setSelectedFace={setSelectedFace}
        closeModalCallback={closeModalCallback}
      />
    </StyledAvatarContainer>
  );
};
